import { MutableRefObject, SetStateAction, useRef, useState } from "react";
import classNames from "classnames";
import { NetworkError } from "@/interfaces/errors/networkError";
import {
  AutocompleteAddress,
  AutocompleteResponse,
} from "@/interfaces/autocomplete";
import useClickOutside from "@/hooks/useClickOutside";
import styles from "./TextInput.module.scss";

const GOUVAPI = process.env.NEXT_PUBLIC_GOUV_API;

export type TextInputProps = {
  value: string;
  setValue: React.Dispatch<SetStateAction<string>>;
  placeholder?: string;
  ref?: MutableRefObject<HTMLInputElement | null>;
  addressAutocomplete?: boolean;
  onSelectAutocompleteAddress?: (address: AutocompleteAddress) => void;
  isError?: boolean;
  isDisabled?: boolean;
};

export const TextInput = ({
  value,
  setValue,
  placeholder,
  ref,
  addressAutocomplete = false,
  onSelectAutocompleteAddress,
  isError = false,
  isDisabled = false,
}: TextInputProps) => {
  const [addressList, setAddressList] = useState<AutocompleteAddress[]>([]);
  const [showList, setShowList] = useState<boolean>(false);

  const refInput = useRef(null);
  useClickOutside(refInput, () => {
    setShowList(false);
  });

  const getAutocompletedAddress = (
    query: string,
    baseURL: string,
    limit = 8
  ) => {
    const requestHeaders: HeadersInit = new Headers();
    requestHeaders.append("Accept", "application/json");

    return fetch(`${baseURL}/search/?q=${query}&limit=${limit}`, {
      method: "GET",
      headers: requestHeaders,
    });
  };

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const onChange = (str: string) => {
    setValue(str);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (addressAutocomplete && str.length > 4 && GOUVAPI !== undefined) {
        getAutocompletedAddress(str, GOUVAPI)
          .then((response: Response) => {
            response
              .json()
              .then((json: AutocompleteResponse) => {
                const result: AutocompleteAddress[] = [];
                json.features.map((feat) => {
                  result.push({
                    label: feat.properties.label,
                    coordinates: feat.geometry.coordinates,
                    number: feat.properties.housenumber,
                    street: feat.properties.street,
                    postcode: feat.properties.postcode,
                    city: feat.properties.city,
                  });
                });
                setAddressList(result);
                setShowList(true);
              })
              .catch((e) => console.error(e));
          })
          .catch((e: NetworkError<null>) => console.error(e));
      } else {
        setAddressList([]);
      }
    }, 400);
  };

  const onSelectAddress = (address: AutocompleteAddress) => {
    if (onSelectAutocompleteAddress) {
      onSelectAutocompleteAddress(address);
    }

    setAddressList([]);
  };

  return (
    <div className={styles.inputContainer} ref={refInput}>
      <input
        className={classNames(styles.input, {
          [styles.error]: isError,
          [styles.disabled]: isDisabled,
        })}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(oc) => onChange(oc.target.value)}
        ref={ref}
      />
      {addressList.length > 0 && showList && (
        <div className={styles.addressList}>
          {addressList.map((address, index) => (
            <div
              className={styles.address}
              key={`address-${index}`}
              onClick={() => onSelectAddress(address)}
            >
              {address.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
